import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Button, Form, Row, Col, Spinner, Alert, Badge, Stack, Modal,
  ProgressBar, ListGroup,
} from 'react-bootstrap';
import api, {
  initializeCurriculum,
  analyseBook,
  getBookAnalysisStatus,
  confirmChapters,
  getBooksReadyStatus,
} from '../../api/api';
import { getCurrentUser } from '../../utils/auth';
import { showToast } from '../../utils/toast';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY   = process.env.REACT_APP_GOOGLE_API_KEY;
const SCOPES    = 'https://www.googleapis.com/auth/drive.readonly';

// How often to poll analysis status (ms)
const POLL_INTERVAL_MS = 3000;

// ─── Analysis status badge ────────────────────────────────────────────────────
const AnalysisBadge = ({ status }) => {
  const map = {
    pending:    { bg: 'secondary', label: 'Not Analysed' },
    analysing:  { bg: 'warning',   label: 'Analysing…'   },
    analysed:   { bg: 'info',      label: 'Analysed ✓'   },
    processing: { bg: 'warning',   label: 'Processing…'  },
    ready:      { bg: 'success',   label: 'Ready ✓'      },
    error:      { bg: 'danger',    label: 'Error'        },
  };
  const { bg, label } = map[status] || map.pending;
  return <Badge bg={bg} className="ms-2">{label}</Badge>;
};

// ─── TOC chapter selector modal ───────────────────────────────────────────────
const TocModal = ({ show, onHide, book, accessToken, tenantSlug, projectId, onConfirmed }) => {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const toc = book?.toc_data || [];
  const allChapters = toc.map(t => t.chapter);

  const toggleAll = () => {
    setSelected(prev =>
      prev.size === allChapters.length ? new Set() : new Set(allChapters)
    );
  };

  const toggle = (ch) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(ch) ? next.delete(ch) : next.add(ch);
      return next;
    });
  };

  const selectionString = () => {
    if (selected.size === 0 || selected.size === allChapters.length) return 'All';
    return [...selected].sort((a, b) => a - b).join(', ');
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      await confirmChapters(tenantSlug, projectId, {
        book_hash:         book.hash,        // references state uses 'hash', not 'book_hash'
        oauth_token:       accessToken,
        selected_chapters: selectionString(),
      });
      onConfirmed(book.hash, selectionString());
      onHide();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // FastAPI 422 returns detail as an array of {type, loc, msg, input} objects.
      // Render it as a readable string rather than passing the raw object to React.
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || JSON.stringify(d)).join('; '));
      } else if (detail && typeof detail === 'object') {
        setError(JSON.stringify(detail));
      } else {
        setError(detail || 'Failed to confirm chapters.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Estimate credit cost from page_count
  const estBlocks  = book ? Math.max(1, Math.ceil((book.page_count || 1) / 950)) : 1;
  const estCredits = estBlocks * 6;

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Select Chapters — {book?.display_name || 'Book'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span className="text-muted small">Total pages: </span>
            <strong>{book?.page_count?.toLocaleString() || '—'}</strong>
            <span className="text-muted small ms-3">Est. blocks: </span>
            <strong>{estBlocks}</strong>
            <span className="text-muted small ms-3">Est. credits: </span>
            <strong className="text-warning">{estCredits}</strong>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={toggleAll}>
            {selected.size === allChapters.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>

        {toc.length === 0 ? (
          <Alert variant="info" role="status">
            No TOC was detected for this book. All pages will be used.
          </Alert>
        ) : (
          <ListGroup variant="flush" style={{ maxHeight: 400, overflowY: 'auto' }}>
            {toc.map(entry => (
              <ListGroup.Item
                key={entry.chapter}
                className="d-flex align-items-center gap-2 py-2 px-3"
                action
                onClick={() => toggle(entry.chapter)}
              >
                <Form.Check
                  type="checkbox"
                  checked={selected.has(entry.chapter)}
                  onChange={() => toggle(entry.chapter)}
                  onClick={e => e.stopPropagation()}
                />
                <span className="text-muted" style={{ minWidth: 32 }}>Ch.{entry.chapter}</span>
                <span>{entry.title}</span>
                {entry.start_page && (
                  <span className="ms-auto text-muted small">p.{entry.start_page}</span>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        <div className="mt-3 p-2 bg-light rounded small text-muted">
          <strong>Selected:</strong>{' '}
          {selected.size === 0
            ? 'None selected (will use All)'
            : selectionString()}
        </div>

        <Alert variant="info" className="mt-3 mb-0 small" role="status">
          If total selected pages exceed 950, multiple cache blocks will be created
          automatically. Each block costs ~6 credits. Final cost is confirmed after
          block creation completes.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleConfirm} disabled={saving}>
          {saving ? <><Spinner size="sm" className="me-1" aria-hidden="true" />Confirming…</> : 'Confirm Chapters'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const CurriculumInitialStep = ({ project, tenantSlug, onSuccess }) => {
  const [mode, setMode]               = useState('manual');
  const [file, setFile]               = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [accessToken, setAccessToken] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Per-book state: book_hash → {driveId, fileName, hash, title, author,
  //   edition, publisher, chapters, analysis_status, page_count, toc_data,
  //   estimated_blocks, estimated_credits}
  const [references, setReferences] = useState([
    {
      id: Date.now(), driveId: null, fileName: null, hash: null,
      title: '', author: '', edition: '', publisher: '',
      chapters: 'All',
      analysis_status: 'pending',
      page_count: null, toc_data: [], estimated_blocks: null, estimated_credits: null,
    },
  ]);

  const [constraints, setConstraints] = useState({
    minModules: 4, unitsPerModule: 4, topicsPerUnit: 6,
    program_level: 'Postgraduate', target_chapters: 'All Chapters',
  });

  // TOC modal state
  const [tocModal, setTocModal] = useState({ show: false, bookIndex: null });

  // Polling refs: hash → intervalId
  const pollRefs = useRef({});

  // ── Wallet ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    api.get(`/tenant/${tenantSlug}/wallet/balance`)
      .then(res => setWalletBalance(res.data.balance || 0))
      .catch(() => {});
    if (window.gapi) {
      window.gapi.load('picker');
    }
  }, [tenantSlug]);

  useEffect(() => {
    if (project.curriculum_workflow_status === 'drafting') setIsInitializing(true);
  }, [project.curriculum_workflow_status]);

  // ── Cleanup polls on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      Object.values(pollRefs.current).forEach(clearInterval);
    };
  }, []);

  // ── Hash generation (SHA-256 via browser crypto) ─────────────────────────────
  const generateFileHash = async (fileId, token) => {
    const res = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const buf = await res.arrayBuffer();
    const hashBuf = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hashBuf))
      .map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // ── Google Picker ────────────────────────────────────────────────────────────
  const createPicker = useCallback((token, index) => {
    const picker = new window.google.picker.PickerBuilder()
      .addView(window.google.picker.ViewId.DOCS)
      .setOAuthToken(token)
      .setDeveloperKey(API_KEY)
      .setCallback(async (data) => {
        if (data.action !== window.google.picker.Action.PICKED) return;
        const doc = data.docs[0];
        setLoading(true);
        try {
          const fileHash = await generateFileHash(doc.id, token);
          setReferences(prev => {
            const next = [...prev];
            next[index] = {
              ...next[index],
              driveId:         doc.id,
              fileName:        doc.name,
              hash:            fileHash,
              title:           next[index].title || doc.name.replace(/\.[^/.]+$/, ''),
              analysis_status: 'pending',
              page_count:      null,
              toc_data:        [],
            };
            return next;
          });
        } finally {
          setLoading(false);
        }
      })
      .build();
    picker.setVisible(true);
  }, []);

  const handlePickerAuth = useCallback((index) => {
    // Safety check: Ensure Google Identity scripts aren't blocked by ad-blockers
    if (!window.google || !window.google.accounts) {
      showToast("Google Authentication scripts are blocked by your browser. Please disable tracking protection and try again.", 'warning');
      return;
    }
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope:     SCOPES,
      callback:  (response) => {
        if (response.access_token) {
          setAccessToken(response.access_token);
          // 2. Launch the picker using the pre-loaded module
          if (window.gapi && window.gapi.picker) {
            createPicker(response.access_token, index);
          } else {
            // Fallback just in case the background pre-load was too slow
            window.gapi.load('picker', () => createPicker(response.access_token, index));
          }
        }
      },
    });
    client.requestAccessToken();
  }, [createPicker]);

  // ── Reference book CRUD ──────────────────────────────────────────────────────
  const handleAddReference = () => {
    if (references.length < 3) {
      setReferences(prev => [...prev, {
        id: Date.now(), driveId: null, fileName: null, hash: null,
        title: '', author: '', edition: '', publisher: '',
        chapters: 'All', analysis_status: 'pending',
        page_count: null, toc_data: [], estimated_blocks: null, estimated_credits: null,
      }]);
    }
  };

  const handleRemoveReference = (index) => {
    if (references.length > 1) {
      const hash = references[index].hash;
      if (hash && pollRefs.current[hash]) {
        clearInterval(pollRefs.current[hash]);
        delete pollRefs.current[hash];
      }
      setReferences(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateRefField = (index, field, value) => {
    setReferences(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // ── Book analysis polling ─────────────────────────────────────────────────────
  const startPolling = useCallback((bookHash, index) => {
    if (pollRefs.current[bookHash]) return;  // already polling

    const intervalId = setInterval(async () => {
      try {
        const res = await getBookAnalysisStatus(tenantSlug, project.id, bookHash);
        const data = res.data;
        setReferences(prev => {
          const next = [...prev];
          if (next[index]?.hash !== bookHash) return prev;  // stale
          next[index] = {
            ...next[index],
            analysis_status:  data.status,
            page_count:       data.page_count,
            toc_data:         data.toc || [],
            estimated_blocks: data.estimated_blocks,
            estimated_credits:data.estimated_credits,
          };
          return next;
        });
        // Stop polling when terminal state reached
        if (['analysed', 'ready', 'error'].includes(data.status)) {
          clearInterval(pollRefs.current[bookHash]);
          delete pollRefs.current[bookHash];
        }
      } catch {
        clearInterval(pollRefs.current[bookHash]);
        delete pollRefs.current[bookHash];
      }
    }, POLL_INTERVAL_MS);

    pollRefs.current[bookHash] = intervalId;
  }, [tenantSlug, project.id]);

  // ── Analyse Book ──────────────────────────────────────────────────────────────
  const handleAnalyseBook = async (index) => {
    const ref = references[index];
    if (!ref.driveId || !ref.hash || !accessToken) return;

    updateRefField(index, 'analysis_status', 'analysing');

    try {
      await analyseBook(tenantSlug, project.id, {
        drive_id:      ref.driveId,
        oauth_token:   accessToken,
        book_hash:     ref.hash,
        title:         ref.title || ref.fileName,
        book_sequence: index + 1,   // 1-indexed position; persisted so blocks inherit correct order
      });
      // Start polling for result
      startPolling(ref.hash, index);
    } catch (e) {
      updateRefField(index, 'analysis_status', 'error');
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
        : (typeof detail === 'object' && detail ? JSON.stringify(detail) : detail);
      showToast(msg || 'Analysis failed. Please try again.', 'error');
    }
  };

  // ── TOC modal confirm callback ────────────────────────────────────────────────
  const handleChaptersConfirmed = (bookHash, chaptersStr) => {
    setReferences(prev => prev.map(r =>
      r.hash === bookHash
        ? { ...r, chapters: chaptersStr, analysis_status: 'processing' }
        : r
    ));
    // Poll for 'ready' status after block processing
    const index = references.findIndex(r => r.hash === bookHash);
    if (index >= 0) startPolling(bookHash, index);
  };

  // ── All books ready? (gates submit button for ai_grounded mode) ───────────────
  const allBooksReady = references
    .filter(r => r.driveId)
    .every(r => r.analysis_status === 'ready');

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleInitialSubmit = async () => {
    setLoading(true);
    setIsInitializing(true);
    setSubmitError('');
    try {
      const user = getCurrentUser();
      const validBooks = references.filter(r => r.driveId && r.hash);

      if (mode === 'ai_grounded') {
        await initializeCurriculum(tenantSlug, project.id, {
          mode:          'ai_grounded',
          tenant_id:     project.tenant_id,
          oauth_token:   accessToken,
          books:         validBooks,
          book_hash:     validBooks[0]?.hash,
          chapters:      constraints.target_chapters,
          program_level: constraints.program_level,
          user_id:       user.id,
          constraints,
        });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mode', mode);
        formData.append('tenant_id', project.tenant_id);
        formData.append('user_id', user.id);
        formData.append('reference_books_json', JSON.stringify(validBooks));
        formData.append('constraints', JSON.stringify(constraints));
        if (accessToken) formData.append('oauth_token', accessToken);
        await api.post(`/tenant/${tenantSlug}/projects/${project.id}/manual-creation`, formData);
      }
      onSuccess();
    } catch (e) {
      if (e.response?.status !== 409 && e.response?.status !== 202) {
        setSubmitError(e.response?.data?.detail || 'Initialization failed.');
        setIsInitializing(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const isLocked = isInitializing
    || project.curriculum_workflow_status === 'drafting'
    || project.curriculum_mode === 'ai_grounded';

  const submitDisabled =
    isLocked
    || ((mode === 'manual' || mode === 'full_manual') && !file)
    || (mode === 'ai_grounded' && (!references[0].driveId || !references[0].title))
    || (mode === 'manual' && (!references[0].driveId || !references[0].title))
    || (mode === 'ai_grounded' && !allBooksReady);

  // Total estimated credits across all ready books
  const totalEstCredits = references
    .filter(r => r.estimated_credits)
    .reduce((s, r) => s + r.estimated_credits, 0);

  const activeTocBook = tocModal.bookIndex !== null ? references[tocModal.bookIndex] : null;

  return (
    <>
      {/* TOC Chapter Selector Modal */}
      {activeTocBook && (
        <TocModal
          show={tocModal.show}
          onHide={() => setTocModal({ show: false, bookIndex: null })}
          book={activeTocBook}
          accessToken={accessToken}
          tenantSlug={tenantSlug}
          projectId={project.id}
          onConfirmed={handleChaptersConfirmed}
        />
      )}

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">Curriculum Preparation (Select One Option)</h5>
            <div className="d-flex gap-2 align-items-center">
              {totalEstCredits > 0 && (
                <Badge bg="warning" text="dark" className="p-2">
                  Est. Cache Cost: {totalEstCredits} Credits
                </Badge>
              )}
              <Badge bg={walletBalance < 1 ? 'danger' : 'success'} className="p-2">
                Wallet: {walletBalance} Credits
              </Badge>
            </div>
          </div>

          {/* Mode Selector */}
          <Row className="mb-4">
            <Col md={4} onClick={() => setMode('manual')}>
              <Card className={`p-3 cursor-pointer h-100 ${mode === 'manual' ? 'border-primary bg-light shadow-sm' : 'border-dashed'}`}>
                <Form.Check type="radio" checked={mode === 'manual'} readOnly
                  label={<span className="fw-bold">Existing Curriculum + Reference Book(s)</span>} />
                <p className="small text-muted mb-0 mt-2">Upload existing DOCX curriculum; ground with reference books.</p>
              </Card>
            </Col>
            <Col md={4} onClick={() => setMode('full_manual')}>
              <Card className={`p-3 cursor-pointer h-100 ${mode === 'full_manual' ? 'border-primary bg-light shadow-sm' : 'border-dashed'}`}>
                <Form.Check type="radio" checked={mode === 'full_manual'} readOnly
                  label={<span className="fw-bold text-primary">Existing Curriculum — Faculty Materials Only</span>} />
                <p className="small text-muted mb-0 mt-2">Upload DOCX; ground with faculty PPTs or PDFs later.</p>
              </Card>
            </Col>
            <Col md={4} onClick={() => setMode('ai_grounded')}>
              <Card className={`p-3 cursor-pointer h-100 ${mode === 'ai_grounded' ? 'border-primary bg-light shadow-sm' : 'border-dashed'}`}>
                <Form.Check type="radio" checked={mode === 'ai_grounded'} readOnly
                  label={<span className="fw-bold text-success">Full AI Curriculum from Reference Books</span>} />
                <p className="small text-muted mb-0 mt-2">Curriculum designed entirely from selected textbooks via AI.</p>
              </Card>
            </Col>
          </Row>

          {/* DOCX Upload (manual modes) */}
          {(mode === 'manual' || mode === 'full_manual') && (
            <Form.Group className="mb-4 p-3 border rounded bg-white shadow-sm">
              <Form.Label className="fw-bold">Step 1: Upload Legacy Curriculum (.docx)</Form.Label>
              <Form.Control type="file" accept=".docx" onChange={e => setFile(e.target.files[0])} />
              <Form.Text className="text-muted">Used to extract your initial curriculum structure.</Form.Text>
            </Form.Group>
          )}

          {/* Reference Books */}
          {mode !== 'full_manual' && (
            <div className={`mb-4 p-3 border rounded shadow-sm ${mode === 'ai_grounded' ? 'border-success' : 'bg-light'}`}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Label className="fw-bold mb-0">
                  Reference Books (Up to 3)
                  {mode === 'ai_grounded' && (
                    <span className="text-muted fw-normal small ms-2">
                      — Analyse each book before initiating curriculum
                    </span>
                  )}
                </Form.Label>
                {references.length < 3 && (
                  <Button variant="outline-primary" size="sm" onClick={handleAddReference}>
                    + Add Book
                  </Button>
                )}
              </div>

              <Stack gap={3}>
                {references.map((ref, index) => (
                  <div key={ref.id} className="p-3 bg-white border rounded">
                    {/* File picker row */}
                    <Row className="g-2 mb-2">
                      <Col md={8}>
                        {ref.fileName ? (
                          <Alert variant="success" className="py-1 px-2 mb-0 small d-flex justify-content-between align-items-center" role="status">
                            <span className="fw-bold text-truncate" style={{ maxWidth: '75%' }}>
                              {ref.fileName}
                            </span>
                            <Button variant="link" size="sm" className="p-0 text-decoration-none"
                              onClick={() => handlePickerAuth(index)}>Change</Button>
                          </Alert>
                        ) : (
                          <Button variant="outline-success" size="sm" className="w-100"
                            onClick={() => handlePickerAuth(index)}>
                            <i className="bi bi-google me-2" />Select from Google Drive
                          </Button>
                        )}
                      </Col>

                      {/* Analyse Book button — shown in ai_grounded (mandatory) and manual (recommended) */}
                      {(mode === 'ai_grounded' || mode === 'manual') && ref.driveId && (
                        <Col md={3}>
                          {ref.analysis_status === 'pending' || ref.analysis_status === 'error' ? (
                            <Button
                              variant={ref.analysis_status === 'error' ? 'outline-danger' : 'outline-secondary'}
                              size="sm" className="w-100"
                              onClick={() => handleAnalyseBook(index)}
                              disabled={!accessToken}
                              title={mode === 'manual'
                                ? 'Recommended for large books (>950 pages) to select specific chapters and avoid processing limits'
                                : 'Required — analyse book to extract TOC and confirm chapters'}
                            >
                              {ref.analysis_status === 'error' ? '⚠ Retry Analyse' : '🔍 Analyse Book'}
                            </Button>
                          ) : ref.analysis_status === 'analysing' || ref.analysis_status === 'processing' ? (
                            <Button variant="outline-warning" size="sm" className="w-100" disabled>
                              <Spinner size="sm" className="me-1" aria-hidden="true" />
                              {ref.analysis_status === 'analysing' ? 'Analysing…' : 'Processing…'}
                            </Button>
                          ) : ref.analysis_status === 'analysed' ? (
                            <Button
                              variant="outline-info" size="sm" className="w-100"
                              onClick={() => setTocModal({ show: true, bookIndex: index })}
                            >
                              📋 Select Chapters
                            </Button>
                          ) : ref.analysis_status === 'ready' ? (
                            <Button
                              variant="outline-success" size="sm" className="w-100"
                              onClick={() => setTocModal({ show: true, bookIndex: index })}
                            >
                              ✅ Chapters Set
                            </Button>
                          ) : null}
                        </Col>
                      )}

                      <Col md={1} className="text-end">
                        {references.length > 1 && (
                          <Button variant="link" className="text-danger p-0" size="sm"
                            onClick={() => handleRemoveReference(index)}>×</Button>
                        )}
                      </Col>
                    </Row>

                    {/* Analysis status + page info — shown in ai_grounded and manual */}
                    {(mode === 'ai_grounded' || mode === 'manual') && ref.driveId && (
                      <div className="mb-2 d-flex align-items-center gap-3 flex-wrap">
                        <AnalysisBadge status={ref.analysis_status} />
                        {ref.page_count && (
                          <span className="text-muted small">
                            {ref.page_count.toLocaleString()} pages
                          </span>
                        )}
                        {ref.estimated_blocks && (
                          <span className="text-muted small">
                            {ref.estimated_blocks} cache block{ref.estimated_blocks > 1 ? 's' : ''}
                          </span>
                        )}
                        {ref.estimated_credits && (
                          <Badge bg="warning" text="dark" className="small">
                            ~{ref.estimated_credits} credits
                          </Badge>
                        )}
                        {ref.analysis_status === 'analysed' && (
                          <span className="text-info small fw-semibold">
                            ← Click "Select Chapters" to continue
                          </span>
                        )}
                      </div>
                    )}

                    {/* Chapters display */}
                    {ref.analysis_status === 'ready' && ref.chapters && ref.chapters !== 'All' && (
                      <Alert variant="success" className="py-1 px-2 mb-2 small" role="status">
                        Chapters: {ref.chapters}
                      </Alert>
                    )}

                    {/* Book metadata fields */}
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Control size="sm" placeholder="Book Title *" value={ref.title}
                          onChange={e => updateRefField(index, 'title', e.target.value)} />
                      </Col>
                      <Col md={6}>
                        <Form.Control size="sm" placeholder="Author(s) *" value={ref.author}
                          onChange={e => updateRefField(index, 'author', e.target.value)} />
                      </Col>
                      <Col md={6}>
                        <Form.Control size="sm" placeholder="Edition" value={ref.edition}
                          onChange={e => updateRefField(index, 'edition', e.target.value)} />
                      </Col>
                      <Col md={6}>
                        <Form.Control size="sm" placeholder="Publisher" value={ref.publisher}
                          onChange={e => updateRefField(index, 'publisher', e.target.value)} />
                      </Col>
                    </Row>

                    {/* Manual chapters text field — shown only when analysis has NOT been run.
                         In manual mode: visible before Analyse Book, hidden after TOC picker confirms.
                         In ai_grounded: always hidden (TOC picker is the only way to set chapters). */}
                    {mode !== 'ai_grounded'
                      && !['analysed', 'processing', 'ready'].includes(ref.analysis_status) && (
                      <Row className="g-2 mt-1">
                        <Col md={12}>
                          <Form.Group className="d-flex align-items-center gap-2">
                            <Form.Label className="small fw-bold text-muted mb-0" style={{ minWidth: 110 }}>
                              Target Chapters:
                            </Form.Label>
                            <Form.Control
                              size="sm"
                              placeholder="e.g. 1-5, 8, 12 or 'All'"
                              value={ref.chapters || 'All'}
                              onChange={e => updateRefField(index, 'chapters', e.target.value)}
                            />
                          </Form.Group>
                          <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                            For large books (&gt;950 pages), click "Analyse Book" to select specific
                            chapters via TOC — this avoids processing limits and reduces cost.
                          </Form.Text>
                        </Col>
                      </Row>
                    )}
                  </div>
                ))}
              </Stack>

              {/* Gate explanation */}
              {mode === 'ai_grounded' && !allBooksReady && references.some(r => r.driveId) && (
                <Alert variant="warning" className="mt-3 mb-0 small" role="alert">
                  <strong>All books must be analysed and chapters confirmed</strong> before you can
                  initiate curriculum generation. Click "Analyse Book" for each book, then
                  "Select Chapters" to confirm your selection.
                </Alert>
              )}
              {mode === 'manual' && references.some(r => r.driveId && r.analysis_status === 'pending') && (
                <Alert variant="info" className="mt-3 mb-0 small" role="status">
                  <strong>Tip:</strong> For books larger than 950 pages, click "Analyse Book" to
                  extract the table of contents and select specific chapters. This prevents
                  processing failures and reduces credit usage. You can still submit without
                  analysing — the book will be split automatically in the background.
                </Alert>
              )}
            </div>
          )}

          {/* Constraints */}
          <div className="p-3 border rounded mb-4 bg-white shadow-sm">
            <Row className="g-3">
              <Col md={6}>
                <Form.Label className="small fw-bold">Program Level</Form.Label>
                <Form.Select value={constraints.program_level}
                  onChange={e => setConstraints({ ...constraints, program_level: e.target.value })}>
                  <option value="Doctoral">Doctoral</option>
                  <option value="Postgraduate">Postgraduate</option>
                  <option value="Undergraduate">Undergraduate</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold">Modules</Form.Label>
                <Form.Control type="number" value={constraints.minModules}
                  onChange={e => setConstraints({ ...constraints, minModules: Number(e.target.value) })} />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold">Units/Mod</Form.Label>
                <Form.Control type="number" value={constraints.unitsPerModule}
                  onChange={e => setConstraints({ ...constraints, unitsPerModule: Number(e.target.value) })} />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-bold">Topics/Unit</Form.Label>
                <Form.Control type="number" value={constraints.topicsPerUnit}
                  onChange={e => setConstraints({ ...constraints, topicsPerUnit: Number(e.target.value) })} />
              </Col>
            </Row>
          </div>

          {/* Submit error */}
          {submitError && <Alert variant="danger" className="mb-3" role="alert">{submitError}</Alert>}

          <Button
            variant={mode === 'ai_grounded' ? 'success' : mode === 'full_manual' ? 'info' : 'primary'}
            className="w-100 py-3 fw-bold shadow-sm text-white"
            disabled={submitDisabled}
            onClick={handleInitialSubmit}
          >
            {isLocked || loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" aria-hidden="true" />
                {loading ? 'Processing…' : 'Initializing Project Lab…'}
              </>
            ) : (
              mode === 'full_manual'
                ? 'Initialize Structure & Prepare for Self-Content'
                : 'Confirm & Generate Curriculum'
            )}
          </Button>
        </Card.Body>
      </Card>
    </>
  );
};

export default CurriculumInitialStep;